import React from "react";
import { AdminApis } from "../../apis/adminApi";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ToastContainer, toast } from "react-toastify";
import { NavLink } from "react-router-dom";
import configs from "../../configs";
import CardRenewSubscription from "./CardRenewSubscription";
import {
  FiCopy as FiCopyIcon, FiTrash2, FiExternalLink, FiLayers,
  FiMousePointer, FiAlertTriangle, FiPlus, FiSearch, FiX,
  FiEdit2, FiCheckCircle, FiEye,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

export default function CardMultiLinks() {
  /* ── State ──────────────────────────────────────────────────────── */
  const [effect, setEffect] = React.useState("");
  const [loader, setLoader] = React.useState(true);
  const [data, setData] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");

  /* ── Delete modal ─────────────────────────────────────────────── */
  const [deleteVisible, setDeleteVisible] = React.useState(false);
  const [selectedLink, setSelectedLink] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  /* ── Body scroll lock ─────────────────────────────────────────── */
  React.useEffect(() => {
    document.body.style.overflow = deleteVisible ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [deleteVisible]);

  /* ── Fetch ────────────────────────────────────────────────────── */
  React.useEffect(() => {
    setLoader(true);
    AdminApis.getMultiLinks()
      .then((res) => { if (res?.data) setData(res.data); })
      .finally(() => setLoader(false));
  }, [effect]);

  /* ── Delete ───────────────────────────────────────────────────── */
  const handleDelete = async (e) => {
    e.preventDefault();
    setDeleting(true);
    try {
      const res = await AdminApis.deleteMultiLink(selectedLink?.id);
      if (res?.data) {
        setDeleteVisible(false);
        setEffect("d_" + Date.now());
        toast.success("Multi-link page deleted.");
      }
    } catch { toast.error("Failed to delete."); }
    finally { setDeleting(false); }
  };

  const openDelete = (link) => { setSelectedLink(link); setDeleteVisible(true); };

  const getClickCount = (link) =>
    link?.short_url?.visits?.filter((v) => v?.operating_system !== "0").length || 0;

  /* ── Filtered list ────────────────────────────────────────────── */
  const filteredLinks = React.useMemo(() => {
    const links = data?.multi_link || [];
    if (!searchTerm.trim()) return links;
    return links.filter((l) =>
      Object.values(l).join(" ").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  /* ── Skeleton ─────────────────────────────────────────────────── */
  if (loader) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between">
            <div className="h-8 bg-gray-100 rounded-xl w-40" />
            <div className="h-10 bg-gray-200 rounded-2xl w-36" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="h-12 bg-gray-200" />
                <div className="p-4 space-y-3">
                  <div className="h-3 bg-gray-100 rounded-full w-3/4" />
                  <div className="h-20 bg-gray-100 rounded-xl" />
                  <div className="h-3 bg-gray-100 rounded-full w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (data?.data === "sub_expired") return <CardRenewSubscription />;

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#0071BC]/10 flex items-center justify-center">
              <FiLayers className="text-[#0071BC]" size={17} />
            </div>
            <div>
              <h1 className="text-lg font-extrabold text-gray-900">Multi Links</h1>
              <p className="text-xs text-gray-400 mt-0.5">
                {(data?.multi_link?.length || 0)} {(data?.multi_link?.length || 0) === 1 ? "page" : "pages"} created
              </p>
            </div>
          </div>
          <NavLink to="/create-multi-link">
            <button className="flex items-center gap-2 px-4 py-2.5 bg-[#0071BC] text-white text-sm font-bold rounded-2xl hover:bg-[#005fa0] active:scale-95 transition-all shadow-sm hover:shadow-md">
              <FiPlus size={16} strokeWidth={2.5} />
              Create New
            </button>
          </NavLink>
        </div>

        {/* Info banner */}
        <div className="mt-3 flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-2xl px-3 py-2.5">
          <FaWhatsapp className="text-[#0071BC] flex-shrink-0 mt-0.5" size={13} />
          <p className="text-xs text-gray-600 leading-relaxed">
            Build a simple webpage with multiple WhatsApp links. Customise it with your logo and social media to boost customer engagement.
          </p>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────── */}
      <div className="px-3 sm:px-4 py-5">

        {/* Search */}
        {(data?.multi_link?.length || 0) > 0 && (
          <div className="relative max-w-sm mb-5">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
            <input
              type="text"
              placeholder="Search pages…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all shadow-sm"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <FiX size={12} />
              </button>
            )}
          </div>
        )}

        {/* Cards */}
        {filteredLinks.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredLinks.map((link) => {
              const clicks = getClickCount(link);
              const pageUrl = `${configs?.baseRedirectFront}${link?.name}`;
              return (
                <div key={link.id} className="group bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden flex flex-col">

                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 bg-[#0071BC]">
                    <div className="flex items-center gap-2 min-w-0">
                      <FiLayers className="text-white/70 flex-shrink-0" size={12} />
                      <span className="text-white font-bold text-sm truncate">{link?.name}</span>
                    </div>
                    <CopyToClipboard text={pageUrl} onCopy={() => toast.success("Link copied!")}>
                      <button className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-[11px] font-semibold rounded-full border border-white/30 transition-all active:scale-95">
                        <FiCopyIcon size={10} /> Copy
                      </button>
                    </CopyToClipboard>
                  </div>

                  {/* Body */}
                  <div className="flex-1 flex flex-col p-4 gap-3">

                    {/* Stats row */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400 font-medium">Page URL</span>
                      <div className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${clicks > 0 ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-400"}`}>
                        <FiMousePointer size={9} />
                        {clicks} visit{clicks !== 1 ? "s" : ""}
                      </div>
                    </div>

                    {/* URL chip */}
                    <a href={pageUrl} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5 hover:bg-blue-50 hover:border-blue-100 transition-colors group/link">
                      <FiExternalLink className="text-[#0071BC] flex-shrink-0" size={12} />
                      <span className="text-xs text-[#0071BC] font-semibold truncate group-hover/link:underline">{pageUrl}</span>
                    </a>

                    {/* Visual preview placeholder */}
                    <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border border-blue-100/60 rounded-2xl px-4 py-5 flex flex-col items-center justify-center gap-2 text-center">
                      <div className="w-9 h-9 rounded-xl bg-white/80 shadow-sm flex items-center justify-center mb-1">
                        <FiLayers className="text-[#0071BC]" size={16} />
                      </div>
                      <p className="text-xs font-bold text-gray-600">Multi-Link Page</p>
                      <p className="text-[10px] text-gray-400 leading-relaxed">Multiple WhatsApp contacts<br/>on one branded page</p>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                    <span className="flex items-center gap-1 text-[11px] font-bold text-green-700 bg-green-50 border border-green-100 px-2.5 py-1 rounded-full">
                      <FiCheckCircle size={9} /> Active
                    </span>
                    <div className="flex items-center gap-1">
                      <NavLink to={`/update-multi-link/${link?.id}`}>
                        <button className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-[#0071BC] hover:bg-blue-50 transition-all" title="Edit page">
                          <FiEdit2 size={13} />
                        </button>
                      </NavLink>
                      <button
                        onClick={() => openDelete(link)}
                        className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                        title="Delete"
                      >
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (data?.multi_link?.length || 0) === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="relative mb-6">
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center shadow-inner">
                <FiLayers className="text-[#0071BC]" size={36} />
              </div>
              {/* Floating WhatsApp badges */}
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-xl flex items-center justify-center shadow-md">
                <FaWhatsapp className="text-white" size={14} />
              </div>
              <div className="absolute -bottom-1 -left-2 w-7 h-7 bg-[#0071BC] rounded-xl flex items-center justify-center shadow-md">
                <FaWhatsapp className="text-white" size={11} />
              </div>
            </div>
            <h3 className="text-lg font-extrabold text-gray-900 mb-2">No multi-link pages yet</h3>
            <p className="text-gray-400 text-sm mb-6 max-w-xs leading-relaxed">
              Create a branded page that shows all your WhatsApp numbers in one place — perfect for teams and businesses.
            </p>
            <NavLink to="/create-multi-link">
              <button className="flex items-center gap-2 px-6 py-3 bg-[#0071BC] text-white text-sm font-bold rounded-2xl hover:bg-[#005fa0] active:scale-95 transition-all shadow-md">
                <FiPlus size={15} /> Create First Page
              </button>
            </NavLink>
          </div>
        ) : (
          /* No search results */
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-3xl bg-gray-100 flex items-center justify-center mb-4">
              <FiSearch className="text-gray-300" size={26} />
            </div>
            <h3 className="text-base font-extrabold text-gray-900 mb-1">No pages found</h3>
            <p className="text-gray-400 text-sm mb-4">Try a different search term.</p>
            <button onClick={() => setSearchTerm("")} className="px-5 py-2 text-sm font-bold text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 shadow-sm transition-colors">
              Clear Search
            </button>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          DELETE MODAL
      ══════════════════════════════════════════════════════════════ */}
      {deleteVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteVisible(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden">

            {/* Red header */}
            <div className="bg-red-50 border-b border-red-100 px-5 py-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <FiAlertTriangle className="text-red-500" size={18} />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-extrabold text-gray-900">Delete Page?</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  <span className="font-semibold text-gray-700">{selectedLink?.name}</span> will be permanently removed.
                </p>
              </div>
              <button onClick={() => setDeleteVisible(false)} className="w-7 h-7 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 transition-all flex-shrink-0">
                <FiX size={13} />
              </button>
            </div>

            {/* Warning list */}
            <div className="px-5 py-4">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">What happens:</p>
              <ul className="space-y-2">
                {[
                  "The page will immediately stop working",
                  "All visit analytics will be lost",
                  "The page name becomes available to others",
                  "Any buttons linking to this page will break",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FiX className="text-red-500" size={8} strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Actions */}
            <div className="px-5 pb-5 flex gap-2.5">
              <button
                type="button"
                onClick={() => setDeleteVisible(false)}
                className="flex-1 py-3 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-2xl transition-all"
              >
                Keep Page
              </button>
              <form onSubmit={handleDelete} className="flex-1">
                <button
                  type="submit"
                  disabled={deleting}
                  className="w-full py-3 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-2xl transition-all shadow-md active:scale-[0.98] disabled:opacity-60"
                >
                  {deleting ? "Deleting…" : "Yes, Delete"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={2500} hideProgressBar theme="light" toastStyle={{ borderRadius: "12px" }} />
    </div>
  );
}
